import { useWindowMessageContext } from "@web/context/WindowMessageContext"
import { getWindow } from "@web/utils"
import { useSession } from "next-auth/react"
import { BrowserMessage, BrowserMessageType } from "platform-common/types"
import React, { useEffect } from "react"
import { useChromeExtensionState } from "./useChromeExtensionState"

export function useExtensionCredentialsSync() {
  const syncedCredentials = React.useRef(false)
  const { data: session, status: authStatus } = useSession()
  const { setBeganExtensionAuth } = useWindowMessageContext()
  const { isInstalled: isExtensionInstalled, isLoading: isLoadingExtensionState } = useChromeExtensionState()

  React.useEffect(
    function syncExtensionCredentialsOnLogin() {
      setBeganExtensionAuth(true)

      if (syncedCredentials.current) return
      if (!session) return
      if (authStatus !== "authenticated") return

      syncedCredentials.current = true

      checkForExtensionInstalled()
    },
    [session, authStatus, setBeganExtensionAuth]
  )

  useEffect(
    function handleExtensionStateLoaded() {
      if (isLoadingExtensionState) return

      // if extension is not installed, we tell the context that we're no longer trying to authenticate
      if (!isExtensionInstalled) {
        setBeganExtensionAuth(false)
      }
    },
    [isExtensionInstalled, isLoadingExtensionState, setBeganExtensionAuth]
  )
}

/**
 * Ping the extension to see if it's installed and if it's authenticated.
 */
export function checkForExtensionInstalled() {
  // message to send to injected content script
  const message: BrowserMessage = {
    message: BrowserMessageType.GetExtensionStatus,
  }
  getWindow()?.postMessage(message, "*")
  // response from extension is handled in web/eventListeners.ts -> WindowMessageContext.tsx("GotExtensionStatus")
}
